// COP-kartet: MapLibre GL med APP-6-symboler, deteksjonsmål, CEP-sirkler og
// peile-/siktelinjer — samme situasjonsbilde-logikk som GNÅ-appens kart.
import maplibregl, { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import { featureCollection, sektorPolygon, type GeoFeature, type GeoFeatureCollection, type Track, type CombatReport, type FieldMessage, REPORT_COLOR } from '@skade/contracts';
import { ensureSymbol } from '../symbols.js';
import { addBasemapLayers, addHillshade, addSeamark, BasemapControl, BG_COLOR, type Ekstraoverlegg } from './basemaps.js';
import { alderTekst } from '../tools/units.js';
import { Blinkstyring, type BlinkPunkt } from './blink.js';

export type SelectFn = (t: Track) => void;

/** Rå GPS-avlesning for EGEN posisjon, slik nettleseren ga den. */
export interface EgenFix {
  lon: number;
  lat: number;
  /** Nettleserens oppgitte usikkerhet i meter (kan mangle). */
  noyaktighetM?: number;
  hoydeM?: number;
  fartMs?: number;
  kursGrader?: number;
  /** Måletidspunkt fra GPS-en selv, ikke da vi mottok det. */
  maaltMs: number;
}

/**
 * Det kartet IKKE kan vite om egen enhet: hvem vi er, om posisjonen deles, og
 * hvordan koordinatet skal skrives (MGRS-formateringen eies av `main`).
 * Settes med `settEgenposisjonKontekst`.
 */
export interface EgenposisjonKontekst {
  kallesignal: string;
  /** Deles posisjonen til nettet nå (BFT på)? */
  deles: boolean;
  mgrs: string;
  /** Slå deling av/på fra boblen. Utelates den, vises ingen knapp. */
  vekslDeling?: () => void;
}

/** En ildstøtte-ring: lukket [lon,lat]-polygon + rolle (fareavstand vs. WEZ). */
export interface FireRing {
  ring: [number, number][];
  role: 'danger' | 'wez';
  /**
   * Målet ringen hører til, når den har et.
   *
   * ⚠ HVORFOR DEN MÅTTE FINNES: mållistas «✕» fjernet målet fra lista, men ikke
   * ringen og målkrysset fra kartet — de ble stående for alltid, uten eier.
   * Uten en kobling tilbake til målet fantes det ingen måte å vite HVILKEN ring
   * som hørte til hvilket mål. WEZ-ringer har ingen målreferanse og beholder
   * `undefined`.
   */
  targetId?: string;
}
export interface FireTargetMark { lat: number; lon: number; label: string; targetId?: string; }

const STALE_MS = 300_000; // 5 min — samme som GNÅ-kartet (_kSensorStaleMs)

/** «@ola.nordmann:skade» → «ola.nordmann» for kompakt kart-etikett. */
function shortName(id: string): string {
  return id.replace(/^@/, '').replace(/:.*$/, '');
}

/** Bygger et sirkelpolygon (GeoJSON) fra senter + radius i meter (CEP). */
function circlePolygon(lat: number, lon: number, radiusM: number, steps = 48): number[][] {
  const coords: number[][] = [];
  const latR = radiusM / 111_320;
  const lonR = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180));
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    coords.push([lon + lonR * Math.cos(a), lat + latR * Math.sin(a)]);
  }
  return coords;
}

/** Antall forsøk på å starte GeolocateControl før vi gir opp (à 250 ms). */
const MAKS_GEOLOCATE_FORSOK = 20;

export class CopMap {
  readonly map: MlMap;
  private ready = false;
  private pending: Track[] | null = null;
  private geolocate!: maplibregl.GeolocateControl;
  /** Løpende retry mot GeolocateControl — se `triggerGeolocate`. */
  private geolocateTimer: number | null = null;
  /** Siste EGEN GPS-avlesning — kilden til klikkboblen på den blå prikken. */
  private egenFix: EgenFix | null = null;
  private egenKontekst: ((fix: EgenFix) => EgenposisjonKontekst) | null = null;

  /**
   * `verktoyEierKlikk` er kartets vakt mot at kartets EGNE lag svarer mens et
   * verktøy er i bruk. Uten den fikk operatøren en boble om HV-ansvar eller en
   * feltmelding oppå en påbegynt måling, og pekeren skiftet fra trådkors til
   * hånd — som fikk måleverktøyet til å se ut som det aldri var slått på.
   * Se `map/kartmodus.ts`.
   */
  constructor(
    container: HTMLElement,
    private onSelect: SelectFn,
    private verktoyEierKlikk: () => boolean = () => false,
    /**
     * Overlegg som eies utenfor kartlaget (tråkors, AO-rødmerking), men som skal
     * vises i kartlag-menyen fordi det er DER brukeren leter etter dem.
     */
    private ekstraOverlegg: readonly Ekstraoverlegg[] = [],
  ) {
    this.map = new maplibregl.Map({
      container,
      // Mørk basestil (GNÅ). Ekte basiskart (Kartverket/foto/relieff) legges på
      // som raster-lag i initLayers og veksles via basiskart-velgeren — offline
      // faller COP-en tilbake til den rene bakgrunnsfargen uten å knekke.
      style: {
        version: 8,
        // Fonter for kart-etiketter. demotiles-endepunktet 404-er på navngitte
        // fonter (så etiketter degraderte); openmaptiles' font-CDN serverer
        // «Open Sans Regular» pålitelig. Hentes klient-side (aldri gradert nett).
        glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
        sources: {},
        layers: [{ id: 'bg', type: 'background', paint: { 'background-color': BG_COLOR } }],
      },
      // Oslofjord sensorområde (HV-01): dekker Østfold + Vestfold på begge sider
      // av fjorden. Sentrert midt i fjorden, zoomet ut så hele området vises.
      center: [10.6, 59.35],
      zoom: 9,
      attributionControl: false,
    });
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'top-right');
    this.map.addControl(new maplibregl.ScaleControl({ unit: 'metric', maxWidth: 140 }), 'bottom-left');
    // Egen posisjon (BFT): nettleser-GPS-prikk + sporing.
    this.geolocate = new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
    });
    this.map.addControl(this.geolocate, 'top-right');
    // Egen posisjon er et OBJEKT i situasjonsbildet, ikke bare en prikk: vi tar
    // vare på hele avlesningen (nøyaktighet, høyde, fart, kurs, måletid) og gjør
    // prikken klikkbar. Se `bindEgenPrikk`.
    this.geolocate.on('geolocate', (e: unknown) => {
      const p = e as { coords?: GeolocationCoordinates; timestamp?: number };
      const c = p.coords;
      if (!c) return;
      const tall = (v: number | null | undefined): number | undefined =>
        typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : undefined;
      this.egenFix = {
        lon: c.longitude, lat: c.latitude,
        noyaktighetM: tall(c.accuracy),
        hoydeM: typeof c.altitude === 'number' && Number.isFinite(c.altitude) ? c.altitude : undefined,
        fartMs: tall(c.speed), kursGrader: tall(c.heading),
        // GPS-ens eget tidsstempel når det finnes: alderen skal måles fra
        // MÅLINGEN, ikke fra da nettleseren rakk å levere den til oss.
        maaltMs: typeof p.timestamp === 'number' ? p.timestamp : Date.now(),
      };
      this.bindEgenPrikk();
    });
    // Kildeattribusjon (Kartverket/Esri m.fl.) — kompakt, påkrevd av kildene.
    this.map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    this.map.addControl(new BasemapControl(this.ekstraOverlegg), 'top-left');
    this.map.on('load', () => this.initLayers());
  }

  /**
   * Musepekeren eies av det aktive verktøyet.
   *
   * `mouseenter` på et kartlag satte før pekeren til «pointer» uansett. Under en
   * måling betyr det at trådkorset forsvinner så snart musa passerer et symbol —
   * og et verktøy som mister sin egen peker leses som avslått.
   */
  private settPeker(peker: string) {
    if (this.verktoyEierKlikk()) return;
    this.map.getCanvas().style.cursor = peker;
  }

  private initLayers() {
    // Basiskart NEDERST (før overlaggene) + terreng-relieff, så spor/CEP/grafikk
    // alltid ligger urørt oppå når operatøren veksler bakgrunn.
    addBasemapLayers(this.map);
    addHillshade(this.map);
    addSeamark(this.map);

    this.map.addSource('tracks', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('reports', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('fields', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('cep', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('lines', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('history', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('shared', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('fires', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('sikt', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('blink', { type: 'geojson', data: featureCollection([]) });

    this.map.addSource('arkivdekning', { type: 'geojson', data: featureCollection([]) });
    // Kapabilitetsregisteret (eiermål 2026-08-19): rekkevidde-/identifiserings-
    // sirkler for plasserte sensorer/droner/observasjonsposter. Egen kilde,
    // ikke `cep` — CEP er MÅLT usikkerhet om et SPOR, dette er en KAPABILITETS
    // rekkevidde uavhengig av om noe er detektert der ennå.
    this.map.addSource('kapabiliteter', { type: 'geojson', data: featureCollection([]) });
    // Arkivtreff (SENS-31): HELE det aktuelle søkeutvalget fra observasjonsarkivet,
    // som ETT togglbart lag — ikke sporlaget (`tracks`, dagens/gårsdagens
    // situasjonsbilde). Blander vi arkivtreff inn i sporlaget, ville gamle
    // observasjoner gjort kartet ulesbart for en operatør som bare vil se NÅ.
    // Egen kilde, samme mønster som `kapabiliteter`/`arkivdekning`: geometrien
    // bygges rent i `ui/arkiv.ts` (`arkivtreffFeatureCollection`), her eies bare
    // `setData`.
    this.map.addSource('arkivtreff', { type: 'geojson', data: featureCollection([]) });
    this.map.addSource('ankerlinje', { type: 'geojson', data: featureCollection([]) });

    // ── ARKIVDEKNING — «hvor har vi IKKE sett noe?» (SENS-28) ────────────────
    //
    // Rutenettet fra `arkivDekningshull()`: en celle per rute i utsnittet, med
    // antall arkiverte observasjoner i tidsvinduet. Ligger ALLER NEDERST i
    // lagstakken — det er et bakteppe operatøren leser lendet gjennom, ikke et
    // funn som skal dekke symboler.
    //
    // ⚠ EN TOM CELLE ER EN KUNNSKAPSMANGEL, IKKE EN NEGATIV OBSERVASJON.
    // Derfor er det HULLENE som males (gull = «se hit / stol mindre», systemets
    // ene aksent), mens dekkede celler bare får en dempet kant. Malte vi det
    // motsatt — grønt der vi har sett — ville et tomt kart lest som «avklart»,
    // og det er en aktiv løgn om hva vi vet. Setningen står i panelet ved
    // tallet (`dekningTekst`), fordi en advarsel som ikke er ved tallet ikke er
    // en advarsel.
    //
    // Fargene er `--gold` (#d4a03a) og `--faint` (#4b5b4b) fra `theme.css`
    // skrevet som hex: MapLibre maler i WebGL og kan ikke lese en CSS-variabel.
    // Endres tokenet, endres denne i samme PR — det er samme palett.
    this.map.addLayer({
      id: 'arkivdekning-fill', type: 'fill', source: 'arkivdekning',
      filter: ['==', ['get', 'hull'], true],
      paint: { 'fill-color': '#d4a03a', 'fill-opacity': 0.1 },
    });
    this.map.addLayer({
      id: 'arkivdekning-line', type: 'line', source: 'arkivdekning',
      paint: {
        'line-color': ['case', ['get', 'hull'], '#d4a03a', '#4b5b4b'],
        'line-width': 0.8,
        'line-opacity': ['case', ['get', 'hull'], 0.55, 0.3],
      },
    });

    // Kapabilitetsregisteret: rekkevidde (fylt, tynn kant) + evt. identifiser-
    // ingsrekkevidde (strekk, `kyp` sitt kamera innenfor radaren) + etikett.
    // #5b9bd5 ER `--info` fra theme.css, IKKE en oppfunnet farge — MapLibre
    // maler i WebGL og kan ikke lese en CSS-variabel (§1 i UX_DESIGNSYSTEM.md),
    // samme begrunnelse som CEP/arkivdekning-lagene over. Endres `--info`,
    // endres denne i samme PR. Skiller seg fra CEP (rød, usikkerhet) og
    // arkivdekning (gull/grå, hull) — egen fargekode for «kapabilitet».
    this.map.addLayer({
      id: 'kapabilitet-fill', type: 'fill', source: 'kapabiliteter',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'fill-color': '#5b9bd5', 'fill-opacity': 0.06 },
    });
    this.map.addLayer({
      id: 'kapabilitet-line', type: 'line', source: 'kapabiliteter',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'line-color': '#5b9bd5',
        'line-width': ['case', ['get', 'identifisering'], 1.2, 1.6],
        'line-dasharray': ['case', ['get', 'identifisering'], ['literal', [2, 2]], ['literal', [1]]],
        'line-opacity': 0.7,
      },
    });
    this.map.addLayer({
      id: 'kapabilitet-label', type: 'symbol', source: 'kapabiliteter',
      filter: ['==', ['geometry-type'], 'Point'],
      layout: {
        'text-field': ['get', 'label'], 'text-size': 11, 'text-anchor': 'top', 'text-offset': [0, 0.6],
      },
      paint: { 'text-color': '#5b9bd5', 'text-halo-color': '#0b1210', 'text-halo-width': 1.2 },
    });

    // Arkivtreff (SENS-31): analytikerens søkeutvalg fra observasjonsarkivet,
    // som punkter — HELE utvalget samtidig, ikke fly-til én rad om gangen.
    // #a8b4a8 ER `--silver` fra theme.css, IKKE en oppfunnet farge (samme
    // begrunnelse som kapabilitet/arkivdekning/sikt over — MapLibre maler i
    // WebGL og kan ikke lese en CSS-variabel). Silver er ledig: skilt fra
    // `--info` (kapabiliteter/delte tegninger), `--gold` (arkivdekning/sikt/
    // ankerlinje/blink) og `--sage` (sporhistorikk). Endres `--silver`, endres
    // denne i samme PR.
    this.map.addLayer({
      id: 'arkivtreff-dot', type: 'circle', source: 'arkivtreff',
      paint: {
        'circle-radius': 4,
        'circle-color': '#a8b4a8',
        'circle-opacity': 0.75,
        'circle-stroke-color': '#0a0c0a',
        'circle-stroke-width': 1,
      },
    });

    // ── ANKERLINJER — «det man sier og det man ser er samme sted» ────────────
    //
    // UX-funn **F4** (HØY): «en melding i Feltveksler blir aldri et objekt på
    // kartet, og et spor/kontakt på kartet har ingen egen samtaletråd. To
    // parallelle virkeligheter … Konsekvens: kontekst går tapt; "hvor var det han
    // meldte om?" krever manuell kryssjekk.»
    //
    // Meldingen kan ha `omObjekt` (se contracts), men uten en linje er koblingen
    // usynlig på kartet: rapportsirkelen ligger et sted i nærheten av sporet, og
    // ingenting sier at de hører sammen. Denne linja ER den manuelle kryssjekken,
    // tegnet.
    //
    // ⚠ STIPLET OG DEMPET MED VILJE. Linja er en RELASJON, ikke en bevegelse. En
    // heltrukken linje mellom to punkter leses i et kartbilde som en akse, en rute
    // eller en peiling — altså som taktisk informasjon om lendet. Stiplet og tynt
    // sier «disse to er samme sak» uten å påstå noe om terrenget mellom dem.
    this.map.addLayer({
      id: 'ankerlinje', type: 'line', source: 'ankerlinje',
      paint: {
        'line-color': '#d4a03a',
        'line-width': 1,
        'line-opacity': 0.5,
        'line-dasharray': [2, 2],
      },
    });

    // Sporhistorikk (breadcrumbs) — nederst, dempet.
    this.map.addLayer({
      id: 'history', type: 'line', source: 'history',
      paint: { 'line-color': '#8ba888', 'line-width': 1.5, 'line-opacity': 0.4 },
    });
    // Delte tegninger fra andre operatører (sanntid).
    this.map.addLayer({
      id: 'shared-fill', type: 'fill', source: 'shared',
      filter: ['==', '$type', 'Polygon'],
      paint: { 'fill-color': '#5b9bd5', 'fill-opacity': 0.12 },
    });
    this.map.addLayer({
      id: 'shared-line', type: 'line', source: 'shared',
      paint: { 'line-color': '#5b9bd5', 'line-width': 2 },
    });

    // BLINK — pulserende ring bak et symbol som nettopp har meldt noe.
    //
    // Eierkravet (2026-08-17): «de sensorene som detekterer noe av interesse skal
    // blinke opp i noen sekunder og med en advarsel som lar operatører velge å
    // rette oppmerksomheten mot denne.»
    //
    // ⚠ «LAR OPERATØREN VELGE» ER DET BINDENDE ORDET. Kartet panorerer ALDRI av
    // seg selv hit. En operatør som koordinerer noe annet skal ikke få kartet
    // revet ut av hendene fordi en sensor ropte — blinket tilbyr oppmerksomhet,
    // varselkøen lar henne ta den. Autopanorering ville gjort et varsel til et
    // avbrudd, og avbrudd er hvordan man mister det man holdt på med.
    this.map.addLayer({
      id: 'blink', type: 'circle', source: 'blink',
      paint: {
        // Radius og opasitet drives av `fase` (0→1), satt per animasjonsramme.
        // Ringen vokser UTOVER og fader — samme «hjerteslag» som eieren beskrev.
        'circle-radius': ['+', 8, ['*', 26, ['get', 'fase']]],
        'circle-color': '#d4a03a',
        'circle-opacity': 0,
        'circle-stroke-color': '#d4a03a',
        'circle-stroke-width': 2,
        'circle-stroke-opacity': ['*', 0.85, ['-', 1, ['get', 'fase']]],
      },
    });

    // SIKTSEKTOR — hva en retningsbestemt observasjon SÅ (feltbilde med
    // kompassretning, PTZ-kamera, peileantenne). Ligger under CEP og symbolene.
    //
    // ⚠ GULL, ALDRI RØDT. «Rød = hostil er hellig» (UX §1/§9b): et rødt
    // kakestykke ut fra en EGEN soldats posisjon leses som fiende ved et
    // øyekast. Gull er systemets ene «se hit»-farge og er samme aksent som
    // ESTIMERT/SISTE KJENTE bruker — som er nøyaktig hva en sektor er: et
    // forbehold, ikke et funn.
    //
    // Fargen er `--gold` (#d4a03a) fra `theme.css` skrevet ut som hex, fordi
    // MapLibre maler i WebGL og ikke kan lese en CSS-variabel. Endres tokenet,
    // skal denne endres i samme PR — det er den samme paletten, ikke en egen.
    this.map.addLayer({
      id: 'sikt-fill', type: 'fill', source: 'sikt',
      paint: {
        'fill-color': '#d4a03a',
        // Svakt: sektoren skal si «her så han», ikke dominere lendet under seg.
        // Umålt rekkevidde tegnes ENDA svakere, så en tegnelengde ikke leses
        // som en målt avstand.
        'fill-opacity': ['case', ['get', 'maalt'], 0.13, 0.07],
      },
    });
    this.map.addLayer({
      id: 'sikt-line', type: 'line', source: 'sikt',
      paint: {
        'line-color': '#d4a03a',
        'line-width': 1.2,
        'line-opacity': 0.7,
        // Stiplet når rekkevidden IKKE er målt — samme visuelle språk som
        // `lines`-laget bruker for `estimated`.
        'line-dasharray': ['case', ['get', 'maalt'], ['literal', [1]], ['literal', [3, 2]]],
      },
    });

    // CEP-usikkerhet (nederst).
    this.map.addLayer({
      id: 'cep-fill', type: 'fill', source: 'cep',
      paint: { 'fill-color': '#ff5b5b', 'fill-opacity': 0.12 },
    });
    this.map.addLayer({
      id: 'cep-line', type: 'line', source: 'cep',
      paint: { 'line-color': '#ff5b5b', 'line-opacity': 0.5, 'line-width': 1.5 },
    });
    // Peile-/siktelinjer.
    this.map.addLayer({
      id: 'lines', type: 'line', source: 'lines',
      paint: {
        'line-color': '#ff5b5b',
        'line-opacity': ['case', ['get', 'estimated'], 0.55, 0.8],
        'line-width': 1.5,
        'line-dasharray': ['case', ['get', 'estimated'], ['literal', [2, 2]], ['literal', [1]]],
      },
    });
    // Ildstøtte: fareavstand/WEZ-ringer (fyll + kant). role=danger (rød) eller
    // wez (oransje trussel-ring). Ligger under symbolene.
    this.map.addLayer({
      id: 'fires-fill', type: 'fill', source: 'fires', filter: ['==', '$type', 'Polygon'],
      paint: { 'fill-color': ['match', ['get', 'role'], 'danger', '#ff3b3b', '#ff9a3b'], 'fill-opacity': 0.1 },
    });
    this.map.addLayer({
      id: 'fires-line', type: 'line', source: 'fires', filter: ['==', '$type', 'Polygon'],
      paint: {
        'line-color': ['match', ['get', 'role'], 'danger', '#ff3b3b', '#ff9a3b'],
        'line-width': 1.5, 'line-opacity': 0.85, 'line-dasharray': [3, 2],
      },
    });

    // Symboler (øverst).
    this.map.addLayer({
      id: 'symbols', type: 'symbol', source: 'tracks',
      layout: {
        'icon-image': ['get', 'sidc'],
        'icon-size': 0.6,
        'icon-allow-overlap': true,
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Regular'],
        'text-size': 11,
        'text-offset': [0, 1.4],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': '#c8d8c8',
        'text-halo-color': '#0a0c0a',
        'text-halo-width': 1.5,
        // Tillits-scoring visuelt (STANAG/recipe 4b): lav konfidens → svakere
        // ikon (0.4–1.0); foreldet spor dempes uansett.
        'icon-opacity': ['case', ['get', 'stale'], 0.45, ['+', 0.4, ['*', 0.6, ['get', 'conf']]]],
      },
    });

    // Feltmeldinger (PTT): linje avsender→markør + gul markør i tolket retning.
    this.map.addLayer({
      id: 'field-line', type: 'line', source: 'fields', filter: ['==', '$type', 'LineString'],
      paint: { 'line-color': '#ffd24a', 'line-width': 1.5, 'line-dasharray': [2, 2], 'line-opacity': 0.8 },
    });
    this.map.addLayer({
      id: 'field-dot', type: 'circle', source: 'fields', filter: ['==', '$type', 'Point'],
      paint: {
        'circle-radius': ['match', ['get', 'kind'], 'mark', 7, 4],
        'circle-color': ['match', ['get', 'kind'], 'mark', '#ffd24a', '#80e0ff'],
        'circle-stroke-color': '#0a0c0a', 'circle-stroke-width': 1.5,
      },
    });
    this.map.addLayer({
      id: 'field-label', type: 'symbol', source: 'fields',
      filter: ['all', ['==', '$type', 'Point'], ['==', 'kind', 'mark']],
      layout: { 'text-field': ['get', 'from'], 'text-font': ['Open Sans Regular'], 'text-size': 10, 'text-offset': [0, 1.3], 'text-anchor': 'top', 'text-optional': true },
      paint: { 'text-color': '#ffe9a8', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.5 },
    });
    this.map.on('click', 'field-dot', (e) => {
      if (this.verktoyEierKlikk()) return;
      const p = e.features?.[0]?.properties as { from?: string; text?: string } | undefined;
      if (!p) return;
      const box = document.createElement('div');
      box.className = 'report-popup';
      const t = document.createElement('div'); t.className = 'report-popup-title'; t.style.color = '#ffd24a'; t.textContent = p.from ?? 'Feltmelding';
      const b = document.createElement('div'); b.className = 'thread-text'; b.textContent = p.text ?? '';
      box.append(t, b);
      new maplibregl.Popup({ closeButton: true, offset: 12 }).setLngLat(e.lngLat).setDOMContent(box).addTo(this.map);
    });
    this.map.on('mouseenter', 'field-dot', () => this.settPeker('pointer'));
    this.map.on('mouseleave', 'field-dot', () => this.settPeker(''));

    // Stridsmeldinger (C2): farget markør + type-etikett, klikk = detaljboble.
    this.map.addLayer({
      id: 'reports', type: 'circle', source: 'reports',
      paint: {
        'circle-radius': 9,
        'circle-color': ['get', 'color'],
        'circle-opacity': 0.85,
        'circle-stroke-color': '#0a0c0a',
        'circle-stroke-width': 2,
      },
    });
    this.map.addLayer({
      id: 'reports-label', type: 'symbol', source: 'reports',
      layout: {
        'text-field': ['get', 'type'], 'text-font': ['Open Sans Regular'],
        'text-size': 10, 'text-offset': [0, 1.4], 'text-anchor': 'top', 'text-optional': true,
      },
      paint: { 'text-color': '#e6eee6', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.5 },
    });
    this.map.on('click', 'reports', (e) => {
      if (this.verktoyEierKlikk()) return;
      const raw = (e.features?.[0]?.properties as { report?: string })?.report;
      if (raw) this.onReportSelect?.(JSON.parse(raw) as CombatReport);
    });
    this.map.on('mouseenter', 'reports', () => this.settPeker('pointer'));
    this.map.on('mouseleave', 'reports', () => this.settPeker(''));

    // Ildstøtte: målkryss + etikett (over ringene).
    this.map.addLayer({
      id: 'fires-target', type: 'circle', source: 'fires', filter: ['==', '$type', 'Point'],
      paint: {
        'circle-radius': 7, 'circle-color': '#ff3b3b', 'circle-opacity': 0.9,
        'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2,
      },
    });
    this.map.addLayer({
      id: 'fires-target-label', type: 'symbol', source: 'fires', filter: ['==', '$type', 'Point'],
      layout: {
        'text-field': ['get', 'label'], 'text-font': ['Open Sans Regular'],
        'text-size': 11, 'text-offset': [0, 1.4], 'text-anchor': 'top', 'text-optional': true,
      },
      paint: { 'text-color': '#ffb3b3', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.5 },
    });

    this.map.on('click', 'symbols', (e) => {
      if (this.verktoyEierKlikk()) return;
      const f = e.features?.[0];
      if (f) {
        const raw = (f.properties as { track?: string }).track;
        if (raw) this.onSelect(JSON.parse(raw) as Track);
      }
    });
    this.map.on('mouseenter', 'symbols', () => this.settPeker('pointer'));
    this.map.on('mouseleave', 'symbols', () => this.settPeker(''));

    this.ready = true;
    if (this.pending) {
      this.render(this.pending);
      this.pending = null;
    }
  }

  setLayerVisible(kind: 'sensors' | 'targets', visible: boolean) {
    this._vis[kind] = visible;
    if (this.pending === null) this.render(this._last);
  }
  private _vis = { sensors: true, targets: true };
  private _last: Track[] = [];

  render(tracks: Track[]) {
    this._last = tracks;
    if (!this.ready) {
      this.pending = tracks;
      return;
    }
    const now = Date.now();
    const symFeatures: GeoFeature[] = [];
    const cepFeatures: GeoFeature[] = [];
    const lineFeatures: GeoFeature[] = [];
    const siktFeatures: GeoFeature[] = [];
    const nodeById = new Map<string, Track>();
    for (const t of tracks) if (t.kind === 'sensor') nodeById.set(t.source, t);

    for (const t of tracks) {
      const isTarget = t.kind === 'detection';
      if (isTarget && !this._vis.targets) continue;
      if (!isTarget && t.kind === 'sensor' && !this._vis.sensors) continue;

      ensureSymbol(this.map, t.sidc);
      // Ferskheten følger SPORET når kilden har oppgitt den: en telefon som
      // sender hvert 5. minutt kan ikke måles med samme vindu som en sensor som
      // sender hvert sekund.
      const ferskhet = t.ferskhetMs && t.ferskhetMs > 0 ? t.ferskhetMs : STALE_MS;
      const stale = t.tsMs !== 0 && now - t.tsMs > ferskhet;
      // ET BEVART SPOR SKAL ALDRI SE FERSKT UT NÅR DET ER GAMMELT.
      // Personspor slettes ikke lenger av en tidsavbrudd (eierkrav: siste kjente
      // posisjon skal ligge). Da blir etiketten kritisk: forskjellen mellom «her
      // er hun» og «her VAR hun sist» må være umulig å lese feil, så alderen
      // skrives inn i etiketten på kartet.
      const etikett = stale && t.bevares
        ? `${t.label} · SIST ${alderTekst(now - t.tsMs)}`
        : t.label;
      symFeatures.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [t.lon, t.lat] },
        properties: { sidc: t.sidc, label: etikett, stale, conf: t.confidence ?? 1, track: JSON.stringify(t) },
      });

      // SIKTSEKTOR: tegnes for ALLE spor som har en, ikke bare mål. Et feltbilde
      // er et `point`-spor (der fotografen sto), og det er nettopp DET sporet som
      // bærer synsretningen — en filtrering på `isTarget` her ville skjult
      // sektoren for det vanligste tilfellet vi har.
      const s = t.siktsektor;
      if (s && s.rMaxM > 0) {
        // `Rekkevidde`-teksten settes av `bildeInnTilSpor` i contracts og er den
        // ene kilden til om lengden er målt. Vi leser den framfor å gjette på
        // rMaxM-verdien, som ikke kan skille «målt til 2000 m» fra tegnelengden.
        const maalt = !/ikke målt/i.test(t.extra?.['Rekkevidde'] ?? '');
        siktFeatures.push({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [sektorPolygon(s.origin, s.bearingDeg, s.bearingSigmaDeg, s.rMaxM, s.rMinM)],
          },
          properties: { maalt, track: t.id },
        } as unknown as GeoFeature);
      }

      if (isTarget) {
        if (t.cepM && t.cepM > 0) {
          cepFeatures.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [t.lon, t.lat] }, // erstattes under
            properties: {},
          });
          // Polygon for CEP (fill/line-lag forventer Polygon-geometri).
          (cepFeatures[cepFeatures.length - 1] as unknown as {
            geometry: { type: string; coordinates: number[][][] };
          }).geometry = { type: 'Polygon', coordinates: [circlePolygon(t.lat, t.lon, t.cepM)] };
        }
        const node = nodeById.get(t.source);
        if (node) {
          lineFeatures.push({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [[node.lon, node.lat], [t.lon, t.lat]] },
            properties: { estimated: !!t.estimated },
          });
        }
      }
    }

    (this.map.getSource('tracks') as GeoJSONSource).setData(
      featureCollection(symFeatures) as never,
    );
    (this.map.getSource('cep') as GeoJSONSource).setData(featureCollection(cepFeatures) as never);
    (this.map.getSource('lines') as GeoJSONSource).setData(
      featureCollection(lineFeatures) as never,
    );
    (this.map.getSource('sikt') as GeoJSONSource)?.setData(
      featureCollection(siktFeatures) as never,
    );
  }

  /**
   * Pulserende markører. Tilstanden ligger i `map/blink.ts` (ren + testet); her
   * eies bare animasjonsløkka og `setData` — de to tingene som krever et kart.
   */
  private blink = new Blinkstyring();
  private blinkRaf: number | null = null;

  /** Start pulsering på de oppgitte punktene (idempotent per id — se Blinkstyring). */
  blinkPunkter(punkter: BlinkPunkt[]) {
    this.blink.start(punkter, performance.now());
    if (this.blink.aktiv && this.blinkRaf === null) this.tegnBlink();
  }

  private tegnBlink() {
    const kilde = this.map.getSource('blink') as GeoJSONSource | undefined;
    if (!kilde) { this.blinkRaf = null; return; }
    kilde.setData(this.blink.ramme(performance.now()) as never);
    if (this.blink.aktiv) {
      this.blinkRaf = requestAnimationFrame(() => this.tegnBlink());
    } else {
      // ⚠ SLIPP LØKKA NÅR DET ER TOMT. En requestAnimationFrame-løkke som aldri
      // stopper er en varig CPU-kostnad på en maskin som skal stå i døgn på et
      // kommandoplass-batteri — og den ville holdt GPU-en våken uten å tegne noe.
      this.blinkRaf = null;
    }
  }

  /** Stopp all pulsering (brukes ved bytte av situasjonsbilde/utlogging). */
  stoppBlink() {
    this.blink.clear();
    if (this.blinkRaf !== null) { cancelAnimationFrame(this.blinkRaf); this.blinkRaf = null; }
    (this.map.getSource('blink') as GeoJSONSource | undefined)?.setData(
      featureCollection([]) as never,
    );
  }

  /**
   * Tegn arkivdekningsrutenettet (SENS-28). Geometrien bygges rent i
   * `ui/arkiv.ts` (`dekningsrutenett`) — her eies bare `setData`.
   */
  tegnArkivdekning(fc: GeoFeatureCollection): void {
    (this.map.getSource('arkivdekning') as GeoJSONSource | undefined)?.setData(fc as never);
  }

  /** Fjern rutenettet igjen. */
  skjulArkivdekning(): void {
    (this.map.getSource('arkivdekning') as GeoJSONSource | undefined)?.setData(
      featureCollection([]) as never,
    );
  }

  /**
   * Tegn kapabilitetsregisteret (eiermål 2026-08-19). Geometrien bygges rent i
   * `ui/kapabilitetsplanlegger.ts` (`kapabiliteterFeatureCollection`) — her
   * eies bare `setData`, samme prinsipp som `tegnArkivdekning` over.
   */
  tegnKapabiliteter(fc: GeoFeatureCollection): void {
    (this.map.getSource('kapabiliteter') as GeoJSONSource | undefined)?.setData(fc as never);
  }

  /** Fjern kapabilitetsringene igjen. */
  skjulKapabiliteter(): void {
    (this.map.getSource('kapabiliteter') as GeoJSONSource | undefined)?.setData(
      featureCollection([]) as never,
    );
  }

  /**
   * Tegn arkivtreffene (SENS-31) — HELE det gjeldende søkeutvalget fra
   * observasjonsarkivet, som ett lag. Geometrien bygges rent i `ui/arkiv.ts`
   * (`arkivtreffFeatureCollection`) — her eies bare `setData`, samme prinsipp
   * som `tegnKapabiliteter`/`tegnArkivdekning` over.
   */
  tegnArkivtreff(fc: GeoFeatureCollection): void {
    (this.map.getSource('arkivtreff') as GeoJSONSource | undefined)?.setData(fc as never);
  }

  /** Fjern arkivtrefflaget igjen. */
  skjulArkivtreff(): void {
    (this.map.getSource('arkivtreff') as GeoJSONSource | undefined)?.setData(
      featureCollection([]) as never,
    );
  }

  flyTo(t: Track) {
    this.map.flyTo({ center: [t.lon, t.lat], zoom: 14 });
  }

  /** Abonnér på egen GPS-posisjon (BFT). */
  onOwnPosition(cb: (lon: number, lat: number) => void) {
    this.geolocate.on('geolocate', (e: unknown) => {
      const c = (e as { coords?: { longitude: number; latitude: number } }).coords;
      if (c) cb(c.longitude, c.latitude);
    });
  }

  /**
   * Abonnér på FEIL fra egen GPS. Uten denne endte et avslag i nettleseren som
   * en evig «ukjent» i EGEN-feltet, uten at operatøren fikk vite hvorfor — og
   * «ukjent posisjon» og «nektet posisjon» er to helt ulike ting i felt.
   */
  onOwnPositionError(cb: (grunn: string) => void) {
    this.geolocate.on('error', (e: unknown) => {
      const kode = (e as { code?: number }).code;
      // GeolocationPositionError: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE,
      // 3 = TIMEOUT.
      cb(
        kode === 1 ? 'nettleseren nekter posisjon (gi tilgang i adressefeltet)'
          : kode === 2 ? 'ingen posisjon tilgjengelig fra maskinen'
            : kode === 3 ? 'GPS svarte ikke i tid'
              : 'ukjent posisjonsfeil',
      );
    });
  }

  /**
   * Gi kartet det det trenger for å beskrive EGEN enhet i klikkboblen.
   * Kalles av `main`, som eier kallesignalet, BFT-tilstanden og MGRS-formatet.
   */
  settEgenposisjonKontekst(fn: (fix: EgenFix) => EgenposisjonKontekst) {
    this.egenKontekst = fn;
  }

  /**
   * Gjør den blå GPS-prikken klikkbar.
   *
   * EIERKRAV 2026-08-13: «jeg må kunne trykke på blåprikken for å få info om
   * denne enheten». Prikken er MapLibres egen brukerposisjons-markør (den tegner
   * også nøyaktighetssirkelen og retningen, som vi ikke vil skrive om), men den
   * var ren dekorasjon: et klikk gikk rett gjennom til kartet, som svarte med
   * kommune/HV-ansvar for punktet. Operatøren fikk altså vite hvilket distrikt
   * hun sto i, men ingenting om sin EGEN måling.
   *
   * Vi binder derfor en lytter på markør-elementet. Idempotent, og kalles ved
   * hver fiks: MapLibre lager elementet først ved første posisjon, og bytter det
   * ut om sporingen stoppes og startes igjen.
   */
  private bindEgenPrikk() {
    try {
      const prikk = this.map.getContainer()
        .querySelector('.maplibregl-user-location-dot') as HTMLElement | null;
      if (!prikk || prikk.dataset.skadeBundet === '1') return;
      prikk.dataset.skadeBundet = '1';
      prikk.style.cursor = 'pointer';
      prikk.title = 'Egen posisjon — klikk for detaljer';
      prikk.addEventListener('click', (ev) => {
        // Klikket skal ikke også nå kartet under (som ville lagt kommune-boblen
        // oppå vår egen).
        ev.stopPropagation();
        this.visEgenposisjon();
      });
    } catch { /* ingen container / ingen DOM */ }
  }

  /** Detaljboble for egen posisjon. */
  private visEgenposisjon() {
    const fix = this.egenFix;
    if (!fix) return;
    const k = this.egenKontekst?.(fix);
    const naa = Date.now();
    const box = document.createElement('div');
    box.className = 'report-popup';
    const tittel = document.createElement('div');
    tittel.className = 'report-popup-title';
    tittel.style.color = '#80e0ff';
    tittel.textContent = `EGEN — ${k?.kallesignal ?? 'egen posisjon'}`;
    box.append(tittel);

    const rad = (merke: string, verdi: string) => {
      const r = document.createElement('div');
      r.className = 'egen-rad';
      const m = document.createElement('span'); m.className = 'egen-merke'; m.textContent = merke;
      const v = document.createElement('span'); v.className = 'egen-verdi'; v.textContent = verdi;
      r.append(m, v);
      box.append(r);
    };

    if (k?.mgrs) rad('MGRS', k.mgrs);
    rad('Lat/lon', `${fix.lat.toFixed(5)}, ${fix.lon.toFixed(5)}`);
    // Nøyaktighet er ikke pynt: en fiks på ±2 000 m (nettverksposisjon) ser
    // identisk ut på kartet som en på ±5 m, og må kunne skilles før noen bruker
    // koordinatet til noe.
    rad('Nøyaktighet', fix.noyaktighetM === undefined ? 'ukjent'
      : `±${Math.round(fix.noyaktighetM)} m`);
    if (fix.hoydeM !== undefined) rad('Høyde', `${Math.round(fix.hoydeM)} m`);
    if (fix.fartMs !== undefined) {
      rad('Fart', `${(fix.fartMs * 3.6).toFixed(1)} km/t`);
    }
    if (fix.kursGrader !== undefined) rad('Kurs', `${Math.round(fix.kursGrader)}°`);
    // Grunnlov: alderen på en måling skal ALLTID vises.
    rad('Målt', `${new Date(fix.maaltMs).toISOString().slice(11, 19)}Z · ${alderTekst(naa - fix.maaltMs)} gammel`);
    if (k) {
      rad('Deling', k.deles ? 'BFT PÅ — posisjonen deles til nettet'
        : 'BFT AV — posisjonen deles ikke');
      if (k.vekslDeling) {
        const knapp = document.createElement('button');
        knapp.type = 'button';
        knapp.className = 'egen-knapp';
        knapp.textContent = k.deles ? 'Slutt å dele (BFT av)' : 'Del posisjon (BFT på)';
        knapp.onclick = () => { this.closePopups(); k.vekslDeling?.(); };
        box.append(knapp);
      }
    }
    this.popupAt([fix.lon, fix.lat], box);
  }

  /**
   * Start GPS-sporing (aktiverer GeolocateControl programmatisk).
   *
   * HVORFOR RETRY: `addControl` gjør IKKE kontrollen klar i samme tick.
   * MapLibres `onAdd` venter på en asynkron støttesjekk (`navigator.permissions`)
   * før knappen settes opp, og `trigger()` returnerer da bare `false` med
   * «Geolocate control triggered before added to a map» i konsollen. Kallet fra
   * oppstartsløypa i main.ts traff nettopp det vinduet, så EGEN-posisjon ble
   * aldri startet i det hele tatt — feltet nederst til venstre sto «ukjent» for
   * alltid, og BFT hadde ingen fiks å dele. Vi prøver derfor på nytt til
   * kontrollen svarer, med tak, slik at et ekte avslag ikke blir et evig mas.
   */
  triggerGeolocate() {
    if (this.geolocateTimer !== null) return; // et forsøk pågår alt
    let forsok = 0;
    const prov = () => {
      forsok += 1;
      let ok = false;
      try {
        ok = this.geolocate.trigger();
      } catch {
        ok = false; // bruker avslo / ikke støttet
      }
      // MAKS_GEOLOCATE_FORSOK × 250 ms ≈ 5 s: rikelig for støttesjekken, og
      // kort nok til at vi ikke poller i bakgrunnen på en maskin uten GPS.
      if (ok || forsok >= MAKS_GEOLOCATE_FORSOK) {
        this.geolocateTimer = null;
        return;
      }
      this.geolocateTimer = window.setTimeout(prov, 250);
    };
    prov();
  }

  /** Tegner stridsmeldinger (C2) som fargede markører. */
  /**
   * Tegn linjer fra forankrede meldinger til objektet de handler om (funn F4).
   *
   * ⚠ BARE FOR PAR DER BEGGE FINNES. Er sporet sluppet, tegnes ingen linje —
   * meldingen står fortsatt der med sin frosne etikett («OM SPOR: KJØRETØY 3»),
   * og en linje ut i tomrommet ville påstått at objektet fortsatt er i bildet.
   */
  tegnAnkerlinjer(par: Array<{ fra: [number, number]; til: [number, number] }>) {
    const feats: GeoFeature[] = par.map((p) => ({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [p.fra, p.til] },
      properties: {},
    }));
    (this.map.getSource('ankerlinje') as GeoJSONSource)?.setData(featureCollection(feats) as never);
  }

  renderReports(reports: CombatReport[]) {
    if (!this.ready) return;
    const feats: GeoFeature[] = reports.map((r) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [r.lon, r.lat] },
      properties: { type: r.type, color: REPORT_COLOR[r.type] ?? '#5b9bd5', report: JSON.stringify(r) },
    }));
    (this.map.getSource('reports') as GeoJSONSource)?.setData(featureCollection(feats) as never);
  }

  /** Kalles når en stridsmelding velges på kartet (main åpner tråd-visning). */
  onReportSelect?: (r: CombatReport) => void;

  flyToReport(r: CombatReport) {
    this.map.flyTo({ center: [r.lon, r.lat], zoom: 15 });
    this.highlightAt(r.lon, r.lat);
  }

  /** Tegner feltmeldinger (PTT): avsenderpunkt + gul markør i tolket retning. */
  renderFields(msgs: FieldMessage[]) {
    if (!this.ready) return;
    const feats: GeoFeature[] = [];
    for (const m of msgs) {
      feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [m.lon, m.lat] }, properties: { kind: 'sender', from: shortName(m.author), text: m.text } });
      if (m.markLat !== undefined && m.markLon !== undefined) {
        feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [m.markLon, m.markLat] }, properties: { kind: 'mark', from: shortName(m.author), text: m.text } });
        feats.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: [[m.lon, m.lat], [m.markLon, m.markLat]] }, properties: {} });
      }
    }
    (this.map.getSource('fields') as GeoJSONSource)?.setData(featureCollection(feats) as never);
  }

  /** Tegner ildstøtte-laget: fareavstands-/WEZ-ringer + målkryss. */
  renderFireSupport(rings: FireRing[], targets: FireTargetMark[]) {
    if (!this.ready) return;
    const feats: GeoFeature[] = [];
    for (const r of rings) {
      feats.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [r.ring] }, properties: { role: r.role } });
    }
    for (const t of targets) {
      feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [t.lon, t.lat] }, properties: { label: t.label } });
    }
    (this.map.getSource('fires') as GeoJSONSource)?.setData(featureCollection(feats) as never);
  }

  private hl?: maplibregl.Marker;
  /** Pulserende markør så et valgt objekt straks skiller seg ut blant hundrevis
   * av bokser i samme område. Kun ÉN om gangen. */
  highlightAt(lon: number, lat: number) {
    this.hl?.remove();
    const el = document.createElement('div');
    el.className = 'map-highlight';
    this.hl = new maplibregl.Marker({ element: el }).setLngLat([lon, lat]).addTo(this.map);
  }
  clearHighlight() {
    this.hl?.remove();
    this.hl = undefined;
  }

  private koPopup?: maplibregl.Popup;
  /** Åpner en detaljboble på et koordinat med ferdig DOM-innhold. */
  popupAt(lngLat: maplibregl.LngLatLike, content: HTMLElement) {
    this.koPopup?.remove();
    this.koPopup = new maplibregl.Popup({ closeButton: true, offset: 12 }).setLngLat(lngLat).setDOMContent(content).addTo(this.map);
  }
  /** Lukk detaljboblen (brukes ved lagring/flytting av objekt). */
  closePopups() { this.koPopup?.remove(); this.koPopup = undefined; }

  private pin?: maplibregl.Marker;
  /** Slipper en midlertidig gull-markør (koordinat-hopp / dropp punkt).
   * Kun ÉN pin om gangen — akkumulerer ikke over tid. */
  dropPin(lonlat: [number, number]) {
    this.pin?.remove();
    this.pin = new maplibregl.Marker({ color: '#d4a03a' }).setLngLat(lonlat).addTo(this.map);
  }

  private emMarkers = new Map<string, maplibregl.Marker>();

  /** Setter/fjerner en 112-nødmarkør per avsender (vedvarende til avlyst). */
  setEmergency(from: string, lon: number, lat: number, active: boolean) {
    this.emMarkers.get(from)?.remove();
    this.emMarkers.delete(from);
    if (!active) return;
    const el = document.createElement('div');
    el.className = 'em-beacon';
    el.textContent = '112';
    el.title = `Nødalarm fra ${from}`;
    const m = new maplibregl.Marker({ element: el }).setLngLat([lon, lat]).addTo(this.map);
    this.emMarkers.set(from, m);
  }

  /** Viser en annen operatørs digitale pekepunkt kortvarig (fader ut). */
  showPointer(lon: number, lat: number, from: string) {
    const el = document.createElement('div');
    el.className = 'shared-pointer';
    el.title = from;
    const m = new maplibregl.Marker({ element: el }).setLngLat([lon, lat]).addTo(this.map);
    setTimeout(() => m.remove(), 4000);
  }

  /** Tegner delte tegninger (GeoJSON-features fra andre operatører). */
  renderShared(features: unknown[]) {
    if (!this.ready) return;
    (this.map.getSource('shared') as GeoJSONSource)?.setData({
      type: 'FeatureCollection',
      features: features as never[],
    } as never);
  }

  /** Tegner sporhistorikk (breadcrumbs) fra hubben. */
  renderHistory(history: Record<string, [number, number][]>) {
    if (!this.ready) return;
    const feats: GeoFeature[] = Object.values(history)
      .filter((coords) => coords.length > 1)
      .map((coords) => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {},
      }));
    (this.map.getSource('history') as GeoJSONSource)?.setData(featureCollection(feats) as never);
  }
}
